import {
  Injectable,
  OnInit,
  OnDestroy
} from '@angular/core';
import {
  KeyValue,
  FormGroupState,
  updateGroup,
  validate,
  AbstractControlState,
  FormState,
  Unboxed,
  unbox,
  SetValueAction,
  SetAsyncErrorAction,
  ClearAsyncErrorAction,
  StartAsyncValidationAction
} from 'ngrx-forms';
import { FormRegisterBuilder } from './form-register-builder';
import { DxFormControl } from './form-control';
import { IDxForm } from './form';
import {
  map,
  distinctUntilChanged,
  filter,
  tap,
  takeUntil
} from 'rxjs/operators';
import {
  AbstractForm,
  matchRelativeControlPath,
  toControlPath
} from './abstract-form';
import { DxFormArray } from './form-array';
import {
  DxFormControlDefinition,
  DxFormArrayDefinition,
  DxFormValidatorDefinition
} from './decorators/interfaces';
import {
  controlValidatorWrapper,
  validationResultRoValidationError
} from './helpers';
import { AbstractFormDefinition } from './abstract-form-definition';
import {
  Observable,
  of,
  merge,
  EMPTY
} from 'rxjs';
import { getMetadata } from '@digitaix/helpers';
import { StepControl } from './step-control';
import { STEP_METADATA_KEY } from './decorators/metadata-keys';
import { ValidatorResult } from './decorators/validator';
import { Action } from '@ngrx/store';
import { DxSetValueAction } from './forms.actions';

@Injectable()
export class DxFormGroup<
  FormValue extends KeyValue,
  GroupFormValue extends KeyValue = null,
  RootGroupFormValue extends KeyValue = GroupFormValue
> extends AbstractForm<FormValue, GroupFormValue, RootGroupFormValue>
  implements
    IDxForm<FormValue, GroupFormValue, RootGroupFormValue>,
    OnInit,
    OnDestroy {
  public get __result(): Partial<Unboxed<FormValue>> {
    return Object.entries(this.__controls).reduce(
      (result, [controlId, control]) => {
        if (control instanceof DxFormArray) {
          result[controlId] = control.__result;
        }
        if (control instanceof DxFormGroup) {
          result[controlId] = control.__result;
        }
        if (control instanceof DxFormControl) {
          if (!control.internal) {
            result[controlId] = control.__value;
          }
        }
        return result;
      },
      {}
    );
  }

  public __definition: AbstractFormDefinition<FormValue> | null = null;

  public __controls: {
    [ControlId in keyof FormValue]: IDxForm<FormValue[ControlId], FormValue>
  };

  public __state$: Observable<FormGroupState<FormValue>>;

  public __state: FormGroupState<FormValue>;

  public __steps: StepControl<any>[] = [];

  /**
   * @internal
   */
  public __validateAndUpdateGroup: (
    state: FormGroupState<FormValue>
  ) => FormGroupState<FormValue> | null;

  /**
   * @internal
   */
  public processDecorator(builder: FormRegisterBuilder): void {
    this.logger.trace(
      `[${this.__uuid}::DxFormGroup::processDecorator::${
        this.__controlId
      }] call`
    );

    this.processAsyncValidatorsDecorator(builder);
    this.processOnSetValueDecorator(builder);

    this.__controls = {} as any;

    this.processControlDecorator(builder);
    this.processGroupDecorator(builder);
    this.processArrayDecorator(builder);
  }

  public processControlDecorator(builder: FormRegisterBuilder): void {
    for (const control of builder.controls) {
      this.__controls[control.name] = this.buildFormControl(control);
      this.defineControlProperty(control.name, control.propertyKey);
    }
  }

  public processSteps(): void {

    const steps = getMetadata(STEP_METADATA_KEY, this.constructor.prototype);

    if (steps) {
      for (const [stepIndex, controls] of Object.entries(steps)) {
        this.__steps[Number(stepIndex)] = StepControl.FromControls(
          this.store$,
          ...controls.map(control => this.__controls[control]),
        );
      }
      this.forEachControl((_, control) => {
        if (control instanceof DxFormGroup) {
          if (control.__steps.length) {
            for (let i = 0; i < control.__steps.length; i++) {
              if (control.__steps[i]) {
                if (this.__steps[i]) {
                  this.__steps[i] = StepControl.FromStepControls(
                    this.store$,
                    this.__steps[i],
                    control.__steps[i],
                  )
                } else {
                  this.__steps[i] = control.__steps[i];
                }
              }
            }
          }
        }
      })
    }

  }

  public processGroupDecorator(builder: FormRegisterBuilder): void {
    for (const group of builder.groups) {
      this.__controls[group.name] = this.buildFormGroup(group);
      this.defineControlProperty(group.name, group.propertyKey);
    }
  }

  public processArrayDecorator(builder: FormRegisterBuilder): void {
    for (const array of builder.arrays) {
      // TODO : fix types
      this.__controls[array.name] = this.buildFormArray(array) as any;
      this.defineControlProperty(array.name, array.propertyKey);
    }
  }

  private defineControlProperty(name: string, propertyKey: string) {
    const get = function(this: DxFormGroup<FormValue>) {
      return this.__controls[name] && this.__controls[name].__value;
    };
    const set = function(this: DxFormGroup<FormValue>, value: any) {
      if (this.__controls[name]) {
        this.__controls[name].setValue(value);
      }
    };
    Object.defineProperty(this, propertyKey, {
      get: get.bind(this),
      set: set.bind(this)
    });
  }

  public processGroupValidatorDecorator(
    builder: FormRegisterBuilder,
    groupValidator: {
      [controlId: string]: (state: AbstractControlState<any>) => FormState<any>;
    }
  ): void {
    for (const group of builder.groups) {
      groupValidator[group.name] = (this.__controls[group.name] as DxFormGroup<
        any
      >).__validateAndUpdateGroup;
    }
  }

  public processArrayValidatorDecorator(
    builder: FormRegisterBuilder,
    groupValidator: {
      [controlId: string]: (state: AbstractControlState<any>) => FormState<any>;
    }
  ): void {
    // TODO : implement array validator builder
  }

  public processValidatorDecorator(builder: FormRegisterBuilder): void {
    const groupValidator: {
      [controlId: string]: <V>(state: AbstractControlState<V>) => FormState<V>;
    } = {};

    this.processControlValidatorDecorator(builder, groupValidator);
    this.processGroupValidatorDecorator(builder, groupValidator);

    this.__validateAndUpdateGroup = updateGroup(groupValidator) as any;
  }

  public processControlValidatorDecorator(
    builder: FormRegisterBuilder,
    groupValidator: {
      [controlId: string]: (state: AbstractControlState<any>) => FormState<any>;
    }
  ): void {
    function groupByControlId(
      validators: DxFormValidatorDefinition<any, any>[]
    ): { [key: string]: DxFormValidatorDefinition<any, any>[] } {
      return validators.reduce(
        (result, validator) => ({
          ...result,
          [validator.controlId]: [
            ...(result[validator.controlId] || []),
            validator
          ]
        }),
        {}
      );
    }

    for (const [controlId, validators] of Object.entries<
      DxFormValidatorDefinition<any, any>[]
    >(
      groupByControlId(
        builder.validators.map(validator => {
          const defaultValidatorErrorTemplate = [
            'FORMS',
            this.__formId,
            'VALIDATION',
            this.__localControlId,
            'ERRORS',
            validator.propertyKey
          ]
            .filter(Boolean)
            .join('.');
          return {
            ...validator,
            validatorErrorTemplate:
              validator.validatorErrorTemplate || defaultValidatorErrorTemplate
          };
        })
      )
    )) {
      groupValidator[controlId] = validate(
        validators.map(v =>
          controlValidatorWrapper(v.validator || this[v.propertyKey], v)
        )
      );
    }
  }

  /**
   * @internal
   */
  public buildFormControl(
    definition: DxFormControlDefinition<any>
  ): DxFormControl<any, any> {
    this.logger.trace(
      `[${this.__uuid}::DxFormGroup::buildFormControl::${this.__controlId}::${
        definition.name
      }] call`,
      definition
    );

    const control = new definition.control(
      this.store$,
      this.logger,
      this.injector
    );

    control.setControlId(definition.name, this.__controlId);
    control.__parent = this;
    // TODO : fix types
    control.__root = this.__root || (this as any);
    control.boxed = definition.boxed;
    if (control.boxed && !this.hasOwnProperty(definition.propertyKey)) {
      this.logger.warn(
        `[${this.__uuid}::DxFormGroup::buildFormControl::${this.__controlId}::${
          definition.name
        }] is boxed but has not a defined initial value`
      );
    }
    control.__initial = control.box(
      this.hasOwnProperty(definition.propertyKey)
        ? this[definition.propertyKey]
        : null
    );
    control.readonly = definition.readonly;
    control.required = definition.required;
    control.disabled = definition.disabled;
    control.disabledSelector = definition.disabledSelector;
    control.disabledFunction = definition.disabledFunction;
    control.placeholder = definition.placeholder;
    control.fxFlex = definition.fxFlex;
    control.fxHide = definition.fxHide;
    control.fxHideSelector = definition.fxHideSelector;
    control.label = definition.label;
    control.internal = !!definition.internal;
    control.selectors = definition.selectors || {};
    control.userDefinedProperties = {
      ...control.userDefinedProperties,
      ...definition.userDefinedProperties
    };

    return control;
  }

  public buildFormArray<V>(
    definition: DxFormArrayDefinition<V>
  ): DxFormArray<V[]> {
    this.logger.trace(
      `[${this.__uuid}::DxFormGroup::buildFormArray::${this.__controlId}::${
        definition.name
      }] call`,
      definition
    );

    const array = new definition.array(this.store$, this.logger, this.injector);

    array.setControlId(definition.name, this.__controlId);
    array.__parent = this;
    // TODO : fix types
    array.__root = this.__root || (this as any);
    array.__groupDefinition = {
      propertyKey: definition.propertyKey,
      name: '[]',
      group: definition.group
    };

    return array;
  }

  public init(): void {
    super.init();

    const builder = new FormRegisterBuilder(
      (<any>this).constructor,
      this.logger
    );

    this.logger.debug(
      `[${this.__uuid}::DxFormGroup::init::${
        this.__controlId
      }] create form register builder`,
      builder
    );

    this.processDecorator(builder);

    this.__initial = {} as any;

    for (const control of Object.values(this.__controls)) {
      control.init();
      this.__initial[control.__controlName] = control.__initial;
    }

    // run validator processing after init
    // bc subgroups need to be initials
    this.processValidatorDecorator(builder);
  }

  public processOnSetValue(
    action: SetValueAction<any>
  ): Observable<void | Action> {

    if (!action.controlId.includes(this.__controlId)) {
      return EMPTY;
    }

    this.logger.trace(
      `[${this.__uuid}::DxFormGroup::processOnSetValue::${
        this.__controlId
      }] call`,
      action
    );

    const observables$: Array<Observable<void | Action>> = [];

    for (const [controlPath, setValue] of Object.entries(
      this._groupedOnSetValue
    )) {
      if (
        matchRelativeControlPath(
          controlPath,
          toControlPath(action.controlId),
          this.__controlPath
        )
      ) {
        observables$.push(
          ...setValue.map(av => {
            const result = this[av.propertyKey](unbox(action.value), action);

            return result instanceof Observable ? result : of(result);
          })
        );
      }
    }

    this.forEachControl((_, control) => {
      // TODO : refactor add processOnSetValue support for controls
      if (control.hasOwnProperty('_groupedOnSetValue')) {
        observables$.push(control['processOnSetValue'](action));
      }
    });

    return merge(...observables$);
  }

  public processAsyncValidators(
    action: SetValueAction<any>
  ): Observable<
    SetAsyncErrorAction | ClearAsyncErrorAction | StartAsyncValidationAction
    > {

    if (!action.controlId.includes(this.__controlId)) {
      return EMPTY;
    }

    this.logger.trace(
      `[${this.__uuid}::DxFormGroup::processAsyncValidators::${
        this.__controlId
      }] call`,
      action
    );

    const observables$: Array<
      Observable<
        SetAsyncErrorAction | ClearAsyncErrorAction | StartAsyncValidationAction
        >
      > = [];

    for (const [controlPath, asyncValidator] of Object.entries(
      this._groupedAsyncValidators
    )) {
      if (
        matchRelativeControlPath(
          controlPath,
          toControlPath(action.controlId),
          this.__controlPath
        )
      ) {
        observables$.push(
          ...asyncValidator.map(av => {
            const result = this[av.propertyKey](action);

            const result$: Observable<ValidatorResult> =
                    result instanceof Observable ? result : of(result);

            const controlId = [this.__controlId, av.controlId].join('.');

            return merge(
              of(new StartAsyncValidationAction(controlId, av.propertyKey)),
              result$.pipe(
                map(validationResult =>
                  validationResultRoValidationError(
                    validationResult,
                    av.validatorErrorTemplate,
                    {
                      action,
                      value: this[av.propertyKey],
                      control: this.getControlByControlId(av.controlId)
                    }
                  )
                ),
                map(error => {
                  if (error === null) {
                    return new ClearAsyncErrorAction(controlId, av.propertyKey);
                  } else {
                    return new SetAsyncErrorAction(
                      controlId,
                      av.propertyKey,
                      error
                    );
                  }
                })
              )
            );
          })
        );
      }
    }

    this.forEachControl((_, control) => {
      if (control.hasOwnProperty('_groupedAsyncValidators')) {
        observables$.push(control['processAsyncValidators'](action));
      }
    });

    return merge(...observables$);
  }

  public ngOnInit(): void {
    super.ngOnInit();
    this.logger.trace(
      `[${this.__uuid}::DxFormGroup::ngOnInit::${this.__controlId}] call`,
      this
    );
    this.__value$ = this.__parent
      ? this.__parent.__value$.pipe(
          filter(Boolean),
          map(value => value[this.__controlName]),
          distinctUntilChanged((a, b) => this.isEqual(a, b)),
          filter(Boolean),
          tap(value =>
            this.logger.trace(
              `[${this.__uuid}::DxFormGroup::ngOnInit::${
                this.__controlId
              }] new value`,
              value
            )
          ),
          takeUntil(this.__onDestroy$)
        )
      : this.__definition.formState$.pipe(
          filter(Boolean),
          map((state: FormGroupState<any>) => unbox(state.value)),
          distinctUntilChanged((a, b) => this.isEqual(a, b)),
          filter(Boolean),
          tap(value =>
            this.logger.trace(
              `[${this.__uuid}::DxFormGroup::ngOnInit::${
                this.__controlId
              }] form update`,
              value
            )
          ),
          takeUntil(this.__onDestroy$)
        );

    this.__state$ = this.__parent
      ? this.__parent.__state$.pipe(
          filter(Boolean),
          map((state: FormGroupState<any>) =>
            Array.isArray(state.controls)
              ? state.controls[+this.__controlName]
              : (state.controls[this.__controlName] as any)
          ),
          filter(Boolean),
          takeUntil(this.__onDestroy$)
        )
      : this.__definition.formState$.pipe(takeUntil(this.__onDestroy$));
    this._subscription.add(
      this.__state$.subscribe(state => (this.__state = state))
    );
    this._subscription.add(
      this.__value$.subscribe(value => (this.__value = value))
    );

    for (const control of Object.values(this.__controls)) {
      control.ngOnInit();
    }

    this.processSteps();

    this.__onInit$.next();
  }

  public forEachControl(
    callback: (controlId: string, control: IDxForm<any, any>) => any
  ): void {
    if (this.__controls) {
      Object.entries(this.__controls).forEach(([controlId, control]) =>
        callback.call(this, controlId, control)
      );
    }
  }

  public getControlByControlId(controlId: string): IDxForm<any, any> {
    return this.__controls[controlId];
  }

  public box(value: Partial<Unboxed<FormValue>>): FormValue {
    const form = {};

    for (const [k, v] of Object.entries(value)) {
      if (!this.__controls.hasOwnProperty(k)) {
        this.logger.warn(
          `[${this.__uuid}::DxFormGroup::box::${
            this.__controlId
          }] A control with the name '${k}' is not defined. Therefore this property will be skipped`
        );
        continue;
      }
      form[k] = this.__controls[k].box(v);
    }

    return {
      ...this.__initial,
      ...form
    };
  }

  public setValue(value: Unboxed<FormValue>): void {
    value = unbox(value) as Unboxed<FormValue>;
    const controlsKeys = Object.keys(this.__controls);
    const valueKeys = Object.keys(value);
    if (valueKeys.every(key => controlsKeys.includes(key))) {
      this.store$.dispatch(
        new DxSetValueAction(this.__controlId, this.box(value), `FormGroup(${this.constructor.name})::setValue`)
      );
    } else {
      for (const key of valueKeys) {
        if (!this.__controls.hasOwnProperty(key)) {
          this.logger.warn(
            `[${this.__uuid}::DxFormGroup::setValue::${
              this.__controlId
            }] A control with the name '${key}' is not defined. Therefore this property will be skipped`
          );
        } else {
          this.__controls[key].setValue(value[key]);
        }
      }
    }
  }
}
